import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchFavoriteProfessionals,
  fetchJob,
  fetchRecentProfessionals,
  fetchRecommendedProfessionals,
  inviteFavoriteToJob,
  inviteProfessionalsToJobs,
} from '~/actions';
import { TAB_INDEX } from '~/components/ProfessionalsHub/utils';
import { useIsError, useIsSuccess, useLoading } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { ProfessionTypeEnum } from '~/models';
interface IProps {
  jobId?: string;
  jobIds?: string[];
  onClosePopup: () => void;
  isRefetchJob?: boolean;
  defaultProfessionals?: string[];
  isOpen?: boolean;
  filterProfession?: ProfessionTypeEnum;
}

/**
 * @param {boolean} isRefetchJob - Flag to determine if the job should be refetched.
 * @param {string} jobId - Job ID to send the invitation to. Ignored if `jobIds` is provided.
 * @param {string[]} jobIds - List of job IDs to send invitations to. Takes precedence over `jobId` if defined.
 * @param {function} onClosePopup - Callback function to close the popup.
 * @param {string[]} defaultProfessionals -  default professionals to be selected by default.
 * @param {boolean} isOpen - Flag to determine if the popup is open.
 * @param {ProfessionTypeEnum} filterProfession - Profession type to filter professionals.
 */
export const useInviteProfessional = ({
  isRefetchJob = true,
  jobId,
  jobIds,
  onClosePopup,
  defaultProfessionals,
  isOpen,
  filterProfession,
}: IProps) => {
  const dispatch = useDispatch();
  const { favoriteProfessionals, recentProfessionals, recommendedProfessionals } = useAppSelector(
    (state) => state.user,
  );

  const favoritesListFiltered = useMemo(
    () =>
      favoriteProfessionals.filter((pro: any) =>
        filterProfession ? pro.professions?.includes(filterProfession) : true,
      ),
    [favoriteProfessionals, filterProfession],
  );

  const recentsListFiltered = useMemo(
    () =>
      recentProfessionals.filter((pro: any) =>
        filterProfession ? pro.professional?.professions?.includes(filterProfession) : true,
      ),
    [recentProfessionals, filterProfession],
  );

  const recommendedListFiltered = useMemo(
    () =>
      recommendedProfessionals.filter((pro) =>
        filterProfession ? pro.professions?.includes(filterProfession) : true,
      ),
    [recommendedProfessionals, filterProfession],
  );

  const { isLoading: isInviting } = useLoading([
    'INVITE_FAVORITE_TO_JOB',
    'INVITE_PROFESSIONALS_TO_JOBS',
  ]);
  const { isLoading } = useLoading([
    'FETCH_FAVORITE_PROFESSIONALS',
    'FETCH_JOB_ACTION',
    'FETCH_RECENT_PROFESSIONALS',
    'FETCH_RECOMMENDED_PROFESSIONALS_ACTION',
  ]);

  const { clearError, isError } = useIsError(['INVITE_FAVORITE_TO_JOB']);
  const { clearSuccess, isSuccess } = useIsSuccess(['INVITE_FAVORITE_TO_JOB']);

  const [activeTab, setActiveTab] = useState(TAB_INDEX.FAVORITES_LIST);
  const [selectedProfessionals, setSelectedProfessionals] = useState<Set<string>>(new Set());

  const handleClosePopup = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();
      clearError();
      clearSuccess();
      onClosePopup();
      setActiveTab(0);
      setSelectedProfessionals(new Set());
    },
    [clearError, clearSuccess, onClosePopup],
  );

  useEffect(() => {
    if (!jobId) {
      return;
    }

    if (isRefetchJob) {
      dispatch(fetchJob({ jobId }));
    }

    Promise.all([
      dispatch(fetchFavoriteProfessionals(jobId)),
      dispatch(fetchRecentProfessionals(jobId)),
      dispatch(fetchRecommendedProfessionals()),
    ]);
  }, [dispatch, isRefetchJob, jobId]);

  useEffect(() => {
    if (isOpen && defaultProfessionals) {
      setSelectedProfessionals(new Set(defaultProfessionals));
    }
  }, [isOpen, defaultProfessionals]);

  useEffect(() => {
    if (isError) {
      handleClosePopup();
    }
  }, [handleClosePopup, isError]);

  useEffect(() => {
    if (favoritesListFiltered?.length) {
      setActiveTab(TAB_INDEX.FAVORITES_LIST);
    } else if (recentsListFiltered?.length >= 3) {
      setActiveTab(TAB_INDEX.RECENT_PROFESSIONALS);
    } else if (recommendedListFiltered?.length) {
      setActiveTab(TAB_INDEX.RECOMMENDED_PROS);
    }
  }, [favoritesListFiltered, recentsListFiltered, recommendedListFiltered]);

  const onSelectProfessionals = useCallback((ids: string[], forceAdd: boolean = false) => {
    setSelectedProfessionals((prevSelected) => {
      const newSelected = new Set(prevSelected);

      ids.forEach((id) => {
        if (newSelected.has(id) && !forceAdd) {
          newSelected.delete(id);
        } else {
          newSelected.add(id);
        }
      });

      return newSelected;
    });
  }, []);

  const onSentInvitations = useCallback(() => {
    if (jobIds?.length) {
      dispatch(
        inviteProfessionalsToJobs({
          jobIds,
          professionalIds: [...selectedProfessionals],
          isJobsInvite: true,
        }),
      );

      return;
    }

    dispatch(inviteFavoriteToJob(jobId, [...selectedProfessionals]));
  }, [dispatch, jobId, jobIds, selectedProfessionals]);

  return {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup: handleClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange: setActiveTab,
    favoriteProfessionals: favoritesListFiltered,
    recentProfessionals: recentsListFiltered,
    recommendedProfessionals: recommendedListFiltered,
  };
};
